import { fmtCompactEur, fmtCompactUsers } from '../formatters.js';

function fmtMaybeUsers(n) {
  return n == null || !Number.isFinite(n) ? '—' : fmtCompactUsers(n);
}

function fmtMaybeEur(n) {
  return n == null || !Number.isFinite(n) ? '—' : fmtCompactEur(n);
}

export default function BreakEvenRequirements({ summary }) {
  const req = summary.breakEvenRequirements || {};
  const last = summary.lastMonth || {};
  const scaleCanFix = req.activeCardholders != null;
  const items = [
    {
      label: 'M36 operating gap',
      value: fmtMaybeEur(req.monthlyOperatingGap),
      current: 'Revenue minus all costs',
    },
    {
      label: 'Contribution lift needed',
      value: fmtMaybeEur(req.contributionImprovementNeeded),
      current: 'Monthly, at M36 scale',
    },
    {
      label: 'Active cardholders needed',
      value: fmtMaybeUsers(req.activeCardholders),
      current: scaleCanFix ? fmtMaybeUsers(last.activeCardholders) : 'Unit margin negative',
    },
    {
      label: 'Monthly card GMV needed',
      value: fmtMaybeEur(req.monthlyCardGmv),
      current: scaleCanFix ? fmtMaybeEur(last.gmvTotal) : 'Scale alone cannot solve',
    },
    {
      label: 'Invest balance for wallet costs',
      value: fmtMaybeEur(req.investBalanceToCoverWalletCost),
      current: fmtMaybeEur(last.stablecoinInvestBalance),
    },
    {
      label: 'Max fixed opex at current scale',
      value: fmtMaybeEur(req.maxFixedOpex),
      current: fmtMaybeEur(last.fixedOpexCost),
    },
    {
      label: 'Max App Account CAC',
      value: fmtMaybeEur(req.maxAppAccountCac),
      current: fmtMaybeEur((last.appAccountCacCost ?? 0) / Math.max(1, last.newAppAccounts ?? 1)),
    },
  ];

  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <div>
          <p className="caption text-carbon-400">What must be true</p>
          <h2 className="h3 mt-2">Break-even requirements</h2>
        </div>
        <p className="label text-carbon-500 num-tabular">M36 basis</p>
      </div>

      {!scaleCanFix && (
        <div className="mb-5 rounded-box border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="label text-rose-800">Negative contribution margin</p>
          <p className="body text-rose-900 mt-1">
            At the selected assumptions, every extra cardholder adds more cost than revenue.
            Break-even requires fixing pricing or subsidy economics before scaling acquisition.
          </p>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-7">
        {items.map((item) => (
          <div key={item.label} className="rounded-box bg-carbon-50 border border-carbon-100 p-4">
            <p className="caption text-carbon-400">{item.label}</p>
            <p className="text-2xl font-semibold tracking-h2 num-tabular mt-2 leading-none">
              {item.value}
            </p>
            <p className="label text-carbon-500 num-tabular mt-3">Now {item.current}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
